import {
  CompleteSequenceDocument,
  CompleteSequenceMutation,
  LearnerTrainingStatus
} from '~/data';
import { client } from '~/data/client';
import { SliceCreator } from '../TrainingState';
import { NavigationSlice as Slice, Sequence } from './NavigationSlice';
import { useToastStore } from '~/ui/toast/toastStore';
import { ToastSeverity } from '~/ui/toast/types';

export const createNavigationSlice: SliceCreator<Slice> = (set, get) => ({
  open: false,
  learnerTrainingId: '',
  chapterSequenceId: '',
  title: '',
  duration: 0,
  regionName: '',
  activeItemPosition: -1,
  highestUnlockedPosition: -1,
  items: [],
  status: LearnerTrainingStatus.Unassigned,
  skip: false,
  player: 'standard',
  scorePercent: 0,
  scorePassingPercent: 0,
  policyAcknowledgement: false,
  policyAcknowledgementSignature: false,
  subtitles: false,

  activateSequence: id => {
    const { items } = get();
    const item = items.find(i => i.kind === 'sequence' && i.id === id);
    if (!item) throw Error(`Sequence with id ${id} not found`);
    // if (
    //   item.position > highestUnlockedPosition &&
    //   environment !== Environments.Dev
    // ) {
    //   throw Error('Sequence locked');
    // }
    set({ activeItemPosition: item.position });
  },

  unlockSequence: id => {
    const item = get().items.find(i => i.kind === 'sequence' && i.id === id);
    if (!item) throw Error(`Sequence with id ${id} not found`);
    set({ highestUnlockedPosition: item.position });
  },

  prev: async () => {
    const { items, activeItemPosition } = get();
    let index = items.findIndex(
      i => i.kind === 'sequence' && i.position === activeItemPosition
    );

    if (index < 0) {
      // This changes the status only on the UI so that we can get past the onboarding process.
      // Moving past the first sequence will update the status on the server
      set({ status: LearnerTrainingStatus.InProgress });

      for (index++; index < items.length; index++) {
        const item = items[index];
        if (item.kind === 'sequence') {
          set({ highestUnlockedPosition: item.position });
          return item.id;
        }
      }
    }

    const sequences = items.filter(
      i => i.kind === 'sequence' && i.position < activeItemPosition
    );

    const sequence = sequences[Object.keys(sequences).length - 1];

    if (!sequence) {
      return 'onboarding';
    }

    set({ highestUnlockedPosition: sequence.position });

    return sequence.id;
  },

  next: async () => {
    const { items, activeItemPosition, learnerTrainingId } = get();
    let index = items.findIndex(
      i => i.kind === 'sequence' && i.position === activeItemPosition
    );

    if (index < 0) {
      // This changes the status only on the UI so that we can get past the onboarding process.
      // Moving past the first sequence will update the status on the server
      set({ status: LearnerTrainingStatus.InProgress });

      for (index++; index < items.length; index++) {
        const item = items[index];
        if (item.kind === 'sequence') {
          set({ highestUnlockedPosition: item.position });
          return item.id;
        }
      }
    }

    const result = await client.mutate<CompleteSequenceMutation>({
      mutation: CompleteSequenceDocument,
      variables: {
        learnerTrainingUuid: learnerTrainingId,
        chapterSequenceId: (items[index] as Sequence).chapterSequenceId
      }
    });

    let shouldReturnCurrentActivePosition = false;

    if (
      result?.data?.completeChapterSequence?.richErrors &&
      result?.data?.completeChapterSequence?.richErrors.length > 0
    ) {
      for (const richError of result.data.completeChapterSequence.richErrors) {
        if (richError.type === 'learner_training_completed_too_fast') {
          set({
            shouldDisplayTimerStillRunningModal:
              richError?.data?.should_display_timer_still_running_modal,
            remainingDurationInMinutes:
              richError?.data?.remaining_duration_in_minutes
          });

          shouldReturnCurrentActivePosition = true;
        }
      }
    }

    if (
      result?.data?.completeChapterSequence?.errors &&
      result?.data?.completeChapterSequence?.errors.length > 0
    ) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: result.data.completeChapterSequence.errors[0]
      });

      shouldReturnCurrentActivePosition = true;
    }

    if (shouldReturnCurrentActivePosition) {
      return (
        items.find(
          i => i.kind === 'sequence' && i.position === activeItemPosition
        )?.id || ''
      );
    }

    const nextId =
      result.data?.completeChapterSequence?.learnerTraining?.chapterSequenceId;

    if (result.data?.completeChapterSequence?.learnerTraining?.scorePercent) {
      set({
        scorePercent:
          result.data?.completeChapterSequence?.learnerTraining?.scorePercent
      });
    }

    if (result.data?.completeChapterSequence?.learnerTraining?.certificateUrl) {
      set({
        certificateUrl:
          result.data?.completeChapterSequence?.learnerTraining?.certificateUrl
      });
    }

    if (
      result.data?.completeChapterSequence?.learnerTraining
        ?.featureShowCertificate
    ) {
      set({
        featureShowCertificate:
          result.data?.completeChapterSequence?.learnerTraining
            ?.featureShowCertificate
      });
    }

    if (
      result.data?.completeChapterSequence?.learnerTraining?.scorePassingPercent
    ) {
      set({
        scorePassingPercent:
          result.data?.completeChapterSequence?.learnerTraining
            ?.scorePassingPercent
      });
    }

    if (!nextId) {
      if (
        result.data?.completeChapterSequence?.learnerTraining?.attemptStatus ===
        'failed'
      ) {
        return 'passfail';
      }

      // If there is no id, that means the training is complete. We can send the user to the feedback form!
      // But first, change the status in state...
      set({ status: LearnerTrainingStatus.Completed });
      return 'feedback';
    }
    const sequence = items.find(
      i => i.kind === 'sequence' && i.chapterSequenceId === nextId
    );

    if (!sequence) throw Error('could not find current item...');
    set({ highestUnlockedPosition: sequence.position });

    return sequence.id;
  },

  toggleNav: open => set({ open: open === undefined ? !get().open : open }),

  remainingDurationInMinutes: null,
  shouldDisplayTimerStillRunningModal: false,

  hideTimerStillRunningModal: () =>
    set({ shouldDisplayTimerStillRunningModal: false }),

  getFirstChapterFirstSequenceId: () => {
    const { items } = get();

    if (items.length === 0) return null;

    const firstChapter = items.find(item => item.kind === 'chapter');

    if (!firstChapter) return null;

    const firstSequence = items.find(
      item => item.kind === 'sequence' && item.chapterId === firstChapter.id
    );

    return firstSequence?.id || null;
  }
});
